@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.launch

// ─────────────────────────────────────────────────────────────────────────────
// HomeViewModel — owns all state mutation, delegates I/O to use-cases.
// Compose-UI only reads `state` and calls `dispatch(action)`.
// ─────────────────────────────────────────────────────────────────────────────
class HomeViewModel(
    private val scope: CoroutineScope,
    private val getCategories:       GetCategoriesUseCase       = GetCategoriesUseCase(),
    private val getFeaturedProducts: GetFeaturedProductsUseCase = GetFeaturedProductsUseCase(),
    private val getNutritionists:    GetNutritionistsUseCase    = GetNutritionistsUseCase(),
    private val getPublicPlans:      GetPublicPlansUseCase      = GetPublicPlansUseCase(),
    private val getUrgencyTimer:     GetUrgencyTimerUseCase     = GetUrgencyTimerUseCase(),
    private val getFeedbackImages:   GetFeedbackImagesUseCase   = GetFeedbackImagesUseCase()
) {
    private val _state = mutableStateOf(HomeScreenState())
    val state: State<HomeScreenState> = _state

    // ── Public dispatch entry-point ───────────────────────────────────────
    fun dispatch(action: HomeScreenAction) {
        when (action) {
            is HomeScreenAction.LoadAdhocData         -> { loadAdhocData(); loadFeedbackImages() }
            is HomeScreenAction.LoadSubscriptionData  -> loadSubscriptionData()
            is HomeScreenAction.LoadFeedbackImages    -> loadFeedbackImages()
            is HomeScreenAction.ChangeViewMode        -> changeViewMode(action.mode)
            is HomeScreenAction.TickTimer             -> tickTimer()
            is HomeScreenAction.ClearError            -> clearError()
        }
    }

    // ── Actions ───────────────────────────────────────────────────────────

    private fun loadAdhocData() {
        if (!_state.value.isLoading && _state.value.categories.isNotEmpty()) return // already loaded
        update { copy(isLoading = true, error = null) }

        scope.launch {
            // Parallel fetch — both requests fly at the same time
            val catsDef  = async { getCategories() }
            val prodsDef = async { getFeaturedProducts() }

            val timerState = getUrgencyTimer()

            val cats  = catsDef.await()
            val prods = prodsDef.await()

            // Surface the first error encountered, but still apply partial data
            val firstError = (cats as? Result.Error)?.exception
                ?: (prods as? Result.Error)?.exception

            update {
                copy(
                    categories       = cats.getOrElse(categories),
                    featuredProducts = prods.getOrElse(featuredProducts),
                    urgencyTimer     = timerState,
                    isLoading        = false,
                    error            = firstError
                )
            }
        }
    }

    private fun loadSubscriptionData() {
        if (!_state.value.isSubLoading && _state.value.nutritionists.isNotEmpty()) return
        update { copy(isSubLoading = true) }

        scope.launch {
            val nutritionistsDef = async { getNutritionists() }
            val plansDef         = async { getPublicPlans() }

            val nutritionists = nutritionistsDef.await()
            val plans         = plansDef.await()

            val firstError = (nutritionists as? Result.Error)?.exception
                ?: (plans as? Result.Error)?.exception

            update {
                copy(
                    nutritionists = nutritionists.getOrElse(this.nutritionists),
                    plans         = plans.getOrElse(this.plans),
                    isSubLoading  = false,
                    error         = firstError ?: error   // don't clobber existing error
                )
            }
        }
    }

    private fun changeViewMode(mode: ViewMode) {
        update { copy(viewMode = mode) }
        // Lazy-load subscription data only when the user first switches to it
        if (mode == ViewMode.SUBSCRIPTION && _state.value.nutritionists.isEmpty()) {
            dispatch(HomeScreenAction.LoadSubscriptionData)
        }
    }

    private fun tickTimer() {
        update { copy(urgencyTimer = urgencyTimer.tick()) }
    }

    private fun clearError() {
        update { copy(error = null) }
    }

    private fun loadFeedbackImages() {
        if (_state.value.feedbackImages.isNotEmpty()) return // already loaded
        scope.launch {
            val urls = getFeedbackImages()
            update { copy(feedbackImages = urls) }
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────

    /** Thread-safe state mutation via copy — never mutate _state.value directly elsewhere. */
    private fun update(block: HomeScreenState.() -> HomeScreenState) {
        _state.value = _state.value.block()
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Compose integration — remember the ViewModel across recompositions
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun rememberHomeViewModel(scope: CoroutineScope): HomeViewModel =
    remember(scope) { HomeViewModel(scope) }

